import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { SWIMMING_AGE_CATEGORIES, COMPETITION_SLUG } from "./constants";
import { Spinner } from "reactstrap";

import compOutono from "../../../assets/images/compOutono.png";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";
import CertificatePrintModal from "./components/CertificatePrintModal";

const CompetitionRankingPage = () => {
    const [rankingData, setRankingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtros
    const [genderFilter, setGenderFilter] = useState("ALL");
    const [categoryFilter, setCategoryFilter] = useState("ALL");
    const [searchQuery, setSearchQuery] = useState("");

    // Modal de Certificado
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [isCertModalOpen, setIsCertModalOpen] = useState(false);

    const handleOpenCertificate = (athlete) => {
        setSelectedAthlete(athlete);
        setIsCertModalOpen(true);
    };

    useEffect(() => {
        const fetchRanking = async () => {
            try {
                setLoading(true);
                const docRef = doc(db, "competitions", COMPETITION_SLUG, "ranking", "consolidated");
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    setRankingData(docSnap.data());
                } else {
                    setRankingData({ competitors: [], totalCompetitors: 0, lastUpdatedAt: new Date().toISOString() });
                }
            } catch (err) {
                console.error("Erro ao carregar ranking:", err);
                setError("Não foi possível carregar o placar oficial de resultados. Tente novamente.");
            } finally {
                setLoading(false);
            }
        };

        fetchRanking();
    }, []);

    // Categorias com nadadores ativos
    const activeCategories = useMemo(() => {
        if (!rankingData?.competitors) return [];
        const cats = new Set();
        rankingData.competitors.forEach(c => {
            if (c.category) cats.add(c.category);
        });
        return Array.from(cats).sort((a, b) => {
            const indexA = SWIMMING_AGE_CATEGORIES.findIndex(cat => cat.label === a);
            const indexB = SWIMMING_AGE_CATEGORIES.findIndex(cat => cat.label === b);
            return indexA - indexB;
        });
    }, [rankingData]);

    // Filtragem em memória (Rápido e Barato)
    const filteredCompetitors = useMemo(() => {
        if (!rankingData?.competitors) return [];
        
        let result = [...rankingData.competitors];

        if (genderFilter !== "ALL") {
            result = result.filter(c => c.gender === genderFilter);
        }

        if (categoryFilter !== "ALL") {
            result = result.filter(c => c.category === categoryFilter);
        }

        if (searchQuery.trim() !== "") {
            const query = searchQuery.toLowerCase();
            result = result.filter(c => 
                c.fullName?.toLowerCase().includes(query) ||
                c.firstName?.toLowerCase().includes(query) ||
                c.lastName?.toLowerCase().includes(query)
            );
        }

        // Ordenação adicional: pagos no topo (por distância decrescente), não pagos (com tracinho) embaixo
        result.sort((a, b) => {
            const isPaidA = a.paymentStatus === "approved" || a.paymentStatus === "confirmed";
            const isPaidB = b.paymentStatus === "approved" || b.paymentStatus === "confirmed";

            if (isPaidA && !isPaidB) return -1;
            if (!isPaidA && isPaidB) return 1;
            
            // Se ambos são pagos ou ambos não pagos, mantém a ordenação original (distância decrescente do consolidado)
            return 0;
        });

        // Recalcular colocações relativas na visualização atual filtrada
        return result.map((c, index) => ({
            ...c,
            relativePosition: index + 1
        }));
    }, [rankingData, genderFilter, categoryFilter, searchQuery]);

    // Estatísticas da Competição (Maior Distância, Recordista e seu Treinador)
    const stats = useMemo(() => {
        if (!rankingData?.competitors || rankingData.competitors.length === 0) {
            return { topDistance: 0, topAthleteName: "Nenhum", topTrainerName: "Nenhum" };
        }
        
        // Competidores já vêm ordenados de forma decrescente pela distância na consolidação — buscamos o primeiro pago
        const topPaidCompetitor = rankingData.competitors.find(c => c.paymentStatus === "approved" || c.paymentStatus === "confirmed");
        const topDistance = topPaidCompetitor?.distance || 0;
        const topAthleteName = topPaidCompetitor?.fullName || "--";
        const topTrainerName = topPaidCompetitor?.professor || "--";
        
        return { topDistance, topAthleteName, topTrainerName };
    }, [rankingData]);

    const formattedLastUpdate = useMemo(() => {
        if (!rankingData?.lastUpdatedAt) return "--:--";
        try {
            const d = new Date(rankingData.lastUpdatedAt);
            return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) + "h de " + d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
        } catch (e) {
            return "--:--";
        }
    }, [rankingData]);

    return (
        <div style={{
            minHeight: "100vh",
            backgroundColor: "#fffdf9",
            backgroundImage: "radial-gradient(rgba(74, 44, 17, 0.03) 1px, transparent 0)",
            backgroundSize: "24px 24px",
            color: "#4a2c11",
            fontFamily: "'Outfit', 'Inter', sans-serif",
            paddingBottom: "80px"
        }}>
            
            {/* Placar Header Estilo Competição Natação com Cover Banner Ampliado */}
            <div className="ranking-header-container">
                {/* Imagem do Banner em tamanho grande / Cover */}
                <div className="ranking-cover-image-wrapper">
                    <img 
                        src={compOutono} 
                        alt="Taça Outono" 
                        style={{ 
                            width: "100%", 
                            height: "100%", 
                            objectFit: "cover", 
                            objectPosition: "center top",
                            display: "block"
                        }} 
                    />
                    {/* Degradê escuro sobre a imagem para integrar com o resto da página */}
                    <div style={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "linear-gradient(to bottom, rgba(45, 23, 5, 0.15) 0%, rgba(45, 23, 5, 0.9) 100%)"
                    }} />
                    
                    {/* Link de voltar e Logo no canto do banner */}
                    <div style={{
                        position: "absolute",
                        top: "20px",
                        left: "20px",
                        right: "20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        zIndex: 10
                    }}>
                        <Link to="/" style={{ 
                            textDecoration: "none", 
                            color: "#fff", 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px", 
                            fontWeight: "900", 
                            fontSize: "0.85rem",
                            background: "rgba(45, 23, 5, 0.75)",
                            padding: "8px 16px",
                            borderRadius: "30px",
                            backdropFilter: "blur(5px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#ea580c"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(45, 23, 5, 0.75)"; }}
                        >
                            <i className="mdi mdi-arrow-left" style={{ fontSize: "1.1rem" }} /> PORTAL DO ATLETA
                        </Link>
                        <img 
                            src={neoLogo} 
                            alt="Neo" 
                            style={{ 
                                height: "18px", 
                                opacity: 0.9, 
                                filter: "brightness(0) invert(1)",
                                background: "rgba(45, 23, 5, 0.75)",
                                padding: "6px 12px",
                                borderRadius: "30px",
                                backdropFilter: "blur(5px)",
                                border: "1px solid rgba(255,255,255,0.15)"
                            }} 
                        />
                    </div>

                    {/* Texto sobreposto no rodapé da imagem */}
                    <div style={{
                        position: "absolute",
                        bottom: "25px",
                        left: "25px",
                        right: "25px",
                        zIndex: 10,
                        textAlign: "left"
                    }}>
                        <span style={{ 
                            fontSize: "0.75rem", 
                            color: "#fff", 
                            background: "#ea580c", 
                            padding: "4px 12px", 
                            borderRadius: "20px", 
                            fontWeight: "900",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                            border: "1px solid rgba(255,255,255,0.2)"
                        }} className="ranking-badge-pill">
                            🏆 Placar Oficial de Resultados
                        </span>
                        <h1 className="ranking-title-text">
                            Resultado Final
                        </h1>
                        <p className="ranking-subtitle-text">
                            A Taça Outono 2026 chegou ao fim! Confira a classificação final por categorias.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="ranking-stat-container" style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 20px" }}>
                {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "350px", gap: "15px" }}>
                        <Spinner style={{ color: "#ea580c" }} />
                        <p style={{ color: "#784b22", fontSize: "0.9rem", fontWeight: "bold" }}>Carregando dados das raias de classificação...</p>
                    </div>
                ) : error ? (
                    <div style={{ background: "#fef2f2", border: "1px solid #fee2e2", padding: "30px", borderRadius: "16px", textAlign: "center", marginTop: "50px" }}>
                        <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "2.5rem", color: "#ef4444" }} />
                        <h5 style={{ color: "#991b1b", margin: "10px 0", fontWeight: "bold" }}>Erro de Conexão</h5>
                        <p style={{ color: "#7f1d1d", margin: 0 }}>{error}</p>
                    </div>
                ) : (
                    <>
                        {/* Caixa de Atenção: Aluno Ansioso / Atualização de Hora em Hora */}
                        <div style={{
                            background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
                            border: "2px solid #bbf7d0",
                            borderRadius: "16px",
                            padding: "20px",
                            marginBottom: "30px",
                            boxShadow: "0 10px 25px rgba(22, 163, 74, 0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "20px"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1, minWidth: "280px" }}>
                                <div style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "50%",
                                    background: "#dcfce7",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1.5px solid #86efac",
                                    flexShrink: 0
                                }}>
                                    <i className="mdi mdi-check-decagram" style={{ color: "#16a34a", fontSize: "1.3rem" }} />
                                </div>
                                <div>
                                    <p style={{ margin: "0 0 2px 0", fontWeight: "900", fontSize: "0.95rem", color: "#166534" }}>
                                        ✅ RESULTADO FINAL HOMOLOGADO
                                    </p>
                                    <p style={{ margin: 0, fontSize: "0.82rem", color: "#15803d", lineHeight: 1.4 }}>
                                        A Taça Outono de Natação 2026 está encerrada. Este é o placar oficial e definitivo da competição. Parabéns a todos os atletas!
                                    </p>
                                </div>
                            </div>
                            <div style={{
                                background: "#fff",
                                border: "1px dashed #86efac",
                                padding: "10px 16px",
                                borderRadius: "10px",
                                textAlign: "center"
                            }}>
                                <span style={{ fontSize: "0.7rem", color: "#166534", fontWeight: "bold", textTransform: "uppercase", display: "block" }}>Última Apuração</span>
                                <span style={{ fontSize: "0.95rem", color: "#16a34a", fontWeight: "900" }}>{formattedLastUpdate}</span>
                            </div>
                        </div>

                        {/* Estatísticas do Placar - Maior Marca, Atleta Recordista e seu Treinador */}
                        <div className="ranking-filter-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "30px" }}>
                            {/* Card 1: Maior Marca */}
                            <div style={{
                                background: "#fff",
                                border: "1px solid #ebdcc5",
                                borderRadius: "16px",
                                padding: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                boxShadow: "0 10px 25px rgba(74, 44, 17, 0.03)"
                            }}>
                                <div style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "12px", 
                                    background: "rgba(234, 88, 12, 0.08)", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center", 
                                    border: "1px solid rgba(234, 88, 12, 0.15)", 
                                    flexShrink: 0 
                                }}>
                                    <i className="mdi mdi-trophy-award" style={{ color: "#ea580c", fontSize: "1.5rem" }} />
                                </div>
                                <div>
                                    <span style={{ fontSize: "0.72rem", color: "#784b22", textTransform: "uppercase", fontWeight: "900", letterSpacing: "0.5px" }}>Maior Marca</span>
                                    <h4 style={{ margin: "2px 0 0 0", color: "#4a2c11", fontWeight: "900", fontSize: "1.55rem" }}>
                                        {stats.topDistance} <span style={{ fontSize: "0.9rem", color: "#ea580c", fontWeight: "bold" }}>m</span>
                                    </h4>
                                </div>
                            </div>

                            {/* Card 2: Atleta Recordista */}
                            <div style={{
                                background: "#fff",
                                border: "1px solid #ebdcc5",
                                borderRadius: "16px",
                                padding: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                boxShadow: "0 10px 25px rgba(74, 44, 17, 0.03)"
                            }}>
                                <div style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "12px", 
                                    background: "rgba(217, 119, 6, 0.08)", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center", 
                                    border: "1px solid rgba(217, 119, 6, 0.15)", 
                                    flexShrink: 0 
                                }}>
                                    <i className="mdi mdi-crown" style={{ color: "#d97706", fontSize: "1.5rem" }} />
                                </div>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                    <span style={{ fontSize: "0.72rem", color: "#784b22", textTransform: "uppercase", fontWeight: "900", letterSpacing: "0.5px" }}>Atleta Recordista</span>
                                    <h4 style={{ margin: "2px 0 0 0", color: "#4a2c11", fontWeight: "900", fontSize: "1.2rem", overflow: "hidden", textOverflow: "ellipsis" }} title={stats.topAthleteName}>
                                        {stats.topAthleteName}
                                    </h4>
                                </div>
                            </div>

                            {/* Card 3: Treinador do Atleta */}
                            <div style={{
                                background: "#fff",
                                border: "1px solid #ebdcc5",
                                borderRadius: "16px",
                                padding: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                boxShadow: "0 10px 25px rgba(74, 44, 17, 0.03)"
                            }}>
                                <div style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "12px", 
                                    background: "rgba(74, 44, 17, 0.06)", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center", 
                                    border: "1px solid rgba(74, 44, 17, 0.15)", 
                                    flexShrink: 0 
                                }}>
                                    <i className="mdi mdi-whistle" style={{ color: "#784b22", fontSize: "1.5rem" }} />
                                </div>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                    <span style={{ fontSize: "0.72rem", color: "#784b22", textTransform: "uppercase", fontWeight: "900", letterSpacing: "0.5px" }}>Treinador</span>
                                    <h4 style={{ margin: "2px 0 0 0", color: "#4a2c11", fontWeight: "900", fontSize: "1.2rem", overflow: "hidden", textOverflow: "ellipsis" }} title={stats.topTrainerName}>
                                        {stats.topTrainerName}
                                    </h4>
                                </div>
                            </div>
                        </div>

                        {/* Layout Principal com Placar (Esquerda) e Guia de Categorias (Lateral Direita) */}
                        <div className="ranking-main-grid">
                            
                            {/* Coluna Esquerda: Filtros e Placar */}
                            <div className="ranking-content-column">
                                {/* Filtros em Linha de Alta Fidelidade */}
                                <div style={{
                                    background: "#fff",
                                    border: "1px solid #e2d5c3",
                                    borderRadius: "20px",
                                    padding: "20px",
                                    marginBottom: "25px",
                                    boxShadow: "0 10px 30px rgba(74, 44, 17, 0.04)"
                                }}>
                                    <div className="ranking-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: "15px", alignItems: "center" }}>
                                        {/* Busca por Nome */}
                                        <div className="ranking-filter-item search" style={{ flex: "2", minWidth: "220px" }}>
                                            <label style={{ display: "block", fontSize: "0.75rem", color: "#784b22", fontWeight: "800", marginBottom: "5px", textTransform: "uppercase" }}>Nadador</label>
                                            <div style={{ position: "relative" }}>
                                                <input
                                                    type="text"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    placeholder="Buscar pelo nome..."
                                                    style={{
                                                        width: "100%",
                                                        padding: "9px 12px 9px 42px",
                                                        borderRadius: "8px",
                                                        border: "1.5px solid #ebdcc5",
                                                        fontSize: "0.85rem",
                                                        outline: "none",
                                                        color: "#4a2c11"
                                                    }}
                                                />
                                                <i className="mdi mdi-magnify" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#784b22", fontSize: "1.05rem" }} />
                                            </div>
                                        </div>

                                        {/* Filtro Sexo */}
                                        <div className="ranking-filter-item" style={{ flex: "1", minWidth: "150px" }}>
                                            <label style={{ display: "block", fontSize: "0.75rem", color: "#784b22", fontWeight: "800", marginBottom: "5px", textTransform: "uppercase" }}>Gênero</label>
                                            <select
                                                value={genderFilter}
                                                onChange={(e) => setGenderFilter(e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "9px 12px",
                                                    borderRadius: "8px",
                                                    border: "1.5px solid #ebdcc5",
                                                    fontSize: "0.85rem",
                                                    outline: "none",
                                                    color: "#4a2c11",
                                                    background: "#fff"
                                                }}
                                            >
                                                <option value="ALL">Todos</option>
                                                <option value="M">Masculino</option>
                                                <option value="F">Feminino</option>
                                            </select>
                                        </div>

                                        {/* Filtro Categoria */}
                                        <div className="ranking-filter-item" style={{ flex: "1.5", minWidth: "180px" }}>
                                            <label style={{ display: "block", fontSize: "0.75rem", color: "#784b22", fontWeight: "800", marginBottom: "5px", textTransform: "uppercase" }}>Categoria de Idade</label>
                                            <select
                                                value={categoryFilter}
                                                onChange={(e) => setCategoryFilter(e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "9px 12px",
                                                    borderRadius: "8px",
                                                    border: "1.5px solid #ebdcc5",
                                                    fontSize: "0.85rem",
                                                    outline: "none",
                                                    color: "#4a2c11",
                                                    background: "#fff"
                                                }}
                                            >
                                                <option value="ALL">Todas as Categorias</option>
                                                {activeCategories.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Placar Oficial de Classificação */}
                                <div style={{
                                    background: "#fff",
                                    border: "2px solid #4a2c11",
                                    borderRadius: "16px",
                                    padding: "20px",
                                    boxShadow: "0 15px 40px rgba(74, 44, 17, 0.08)",
                                    overflow: "hidden"
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "3px solid #4a2c11", paddingBottom: "15px", marginBottom: "15px" }}>
                                        <h3 style={{ margin: 0, fontWeight: "900", color: "#4a2c11", fontSize: "1.2rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            🏁 Classificação de Raias
                                        </h3>
                                        <span style={{ fontSize: "0.78rem", color: "#784b22", fontWeight: "bold", background: "#f5ebe0", padding: "4px 12px", borderRadius: "20px" }}>
                                            {filteredCompetitors.length} competidores
                                        </span>
                                    </div>

                                    {filteredCompetitors.length === 0 ? (
                                        <div style={{ textAlign: "center", padding: "50px 20px" }}>
                                            <i className="mdi mdi-pool" style={{ fontSize: "3rem", color: "#c8b195", opacity: 0.6 }} />
                                            <p style={{ margin: "15px 0 0 0", color: "#784b22", fontSize: "0.95rem", fontWeight: "bold" }}>Nenhuma marca registrada para estes filtros.</p>
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }} className="ranking-table">
                                                <thead>
                                                    <tr style={{ color: "#784b22", fontSize: "0.72rem", textTransform: "uppercase", fontWeight: "900", borderBottom: "2px solid #e2d5c3", textAlign: "left" }}>
                                                        <th style={{ padding: "12px 10px", textAlign: "center", width: "80px" }} className="ranking-table-pos">Raia (Pos)</th>
                                                        <th style={{ padding: "12px 15px" }}>Atleta</th>
                                                        <th style={{ padding: "12px 15px" }}>Categoria</th>
                                                        <th className="hide-on-mobile" style={{ padding: "12px 15px" }}>Clube</th>
                                                        <th style={{ padding: "12px 15px", textAlign: "center", width: "110px" }}>Certificado</th>
                                                        <th style={{ padding: "12px 15px", textAlign: "right", width: "150px" }}>Distância</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredCompetitors.map((c) => {
                                                        const isPaid = c.paymentStatus === "approved" || c.paymentStatus === "confirmed";
                                                        return (
                                                            <tr
                                                                key={c.id}
                                                                style={{
                                                                    borderBottom: "1px solid #f5ebe0",
                                                                    transition: "all 0.2s"
                                                                }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.background = "#fffbf5"; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                                            >
                                                                {/* Pos/Raia */}
                                                                <td style={{ padding: "15px 10px", textAlign: "center" }} className="ranking-table-pos">
                                                                    {isPaid ? (
                                                                        <span className="ranking-pos-badge" style={{
                                                                            display: "inline-flex",
                                                                            width: "36px",
                                                                            height: "36px",
                                                                            borderRadius: "50%",
                                                                            background: "#f5ebe0",
                                                                            color: "#784b22",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontWeight: "900",
                                                                            fontSize: "0.85rem",
                                                                            border: "1.5px solid #ebdcc5"
                                                                        }}>
                                                                            #{c.relativePosition}
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ 
                                                                            color: "#c8b195", 
                                                                            fontWeight: "bold",
                                                                            fontSize: "1.1rem" 
                                                                        }} title="Aguardando confirmação de pagamento">
                                                                            --
                                                                        </span>
                                                                    )}
                                                                </td>

                                                                {/* Nome e Info Geral */}
                                                                <td style={{ padding: "15px" }}>
                                                                    <div style={{ lineHeight: 1.2 }}>
                                                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                            <span className="ranking-athlete-name" style={{ fontWeight: "800", color: "#4a2c11", fontSize: "0.95rem" }}>
                                                                                {c.fullName}
                                                                            </span>
                                                                            {c.relativePosition === 1 && isPaid && (
                                                                                <i className="mdi mdi-crown" style={{ color: "#d97706", fontSize: "1.05rem" }} title="Melhor Tempo" />
                                                                            )}
                                                                        </div>
                                                                        <span className="ranking-athlete-sub" style={{ display: "block", fontSize: "0.72rem", color: "#784b22", marginTop: "2px" }}>
                                                                            {c.gender === "M" ? "Masculino" : "Feminino"}
                                                                        </span>
                                                                    </div>
                                                                </td>

                                                                {/* Categoria */}
                                                                <td style={{ padding: "15px" }}>
                                                                    <div style={{ display: "inline-flex", flexDirection: "column" }}>
                                                                        <span className="ranking-category-badge" style={{
                                                                            background: "#fffbf0",
                                                                            color: "#ea580c",
                                                                            fontSize: "0.72rem",
                                                                            fontWeight: "bold",
                                                                            padding: "2px 8px",
                                                                            borderRadius: "6px",
                                                                            border: "1px solid rgba(234, 88, 12, 0.2)",
                                                                            display: "inline-block",
                                                                            textAlign: "center"
                                                                        }}>
                                                                            {c.category}
                                                                        </span>
                                                                    </div>
                                                                </td>

                                                                {/* Clube */}
                                                                <td className="hide-on-mobile" style={{ padding: "15px", fontSize: "0.85rem", color: "#4a2c11", fontWeight: "600" }}>
                                                                    {c.club}
                                                                </td>

                                                                {/* Certificado */}
                                                                <td style={{ padding: "15px", textAlign: "center" }}>
                                                                    {isPaid ? (
                                                                        <button 
                                                                            onClick={() => handleOpenCertificate(c)}
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            style={{
                                                                                borderRadius: "20px",
                                                                                fontSize: "0.72rem",
                                                                                fontWeight: "800",
                                                                                borderColor: "#d4af37",
                                                                                color: "#8b5e3c",
                                                                                backgroundColor: "#fffdf9",
                                                                                padding: "4px 10px"
                                                                            }}
                                                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fff9f0"; e.currentTarget.style.color = "#4a2c11"; }}
                                                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fffdf9"; e.currentTarget.style.color = "#8b5e3c"; }}
                                                                        >
                                                                            🏆 Emitir
                                                                        </button>
                                                                    ) : (
                                                                        <span style={{ color: "#c8b195", fontSize: "0.72rem" }}>Aguardando</span>
                                                                    )}
                                                                </td>

                                                                {/* Distância */}
                                                                <td style={{ padding: "15px", textAlign: "right" }}>
                                                                    {isPaid ? (
                                                                        <span className="ranking-distance-text" style={{ fontSize: "1.3rem", fontWeight: "900", color: "#ea580c", letterSpacing: "-0.5px" }}>
                                                                            {c.distance} <span className="ranking-distance-unit" style={{ fontSize: "0.82rem", fontWeight: "bold", color: "#784b22" }}>m</span>
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ 
                                                                            color: "#c8b195", 
                                                                            fontWeight: "bold",
                                                                            fontSize: "1.1rem" 
                                                                        }} title="Aguardando confirmação de pagamento">
                                                                            --
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Coluna Direita: Guia Lateral de Categorias */}
                            <div className="ranking-sidebar-column">
                                <div style={{
                                    background: "#fff",
                                    border: "2px solid #4a2c11",
                                    borderRadius: "20px",
                                    padding: "20px",
                                    boxShadow: "0 10px 25px rgba(74, 44, 17, 0.03)"
                                }}>
                                    <h4 style={{ margin: "0 0 12px 0", fontWeight: "900", fontSize: "0.95rem", color: "#4a2c11", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "8px", borderBottom: "2px solid #ebdcc5", paddingBottom: "10px" }}>
                                        <i className="mdi mdi-information-outline" style={{ color: "#ea580c", fontSize: "1.1rem" }} /> Categorias por Idade
                                    </h4>
                                    <p style={{ margin: "0 0 15px 0", fontSize: "0.75rem", color: "#784b22", lineHeight: 1.4 }}>
                                        Classificação oficial baseada no ano de nascimento (Regra CBDA 2026).
                                    </p>
                                    <div style={{ 
                                        display: "flex", 
                                        flexDirection: "column", 
                                        gap: "8px",
                                        maxHeight: "560px",
                                        overflowY: "auto",
                                        paddingRight: "6px"
                                    }} className="custom-scroll">
                                        {SWIMMING_AGE_CATEGORIES.map(cat => (
                                            <div key={cat.label} style={{ 
                                                display: "flex", 
                                                justifyContent: "space-between", 
                                                alignItems: "center",
                                                padding: "7px 10px",
                                                borderRadius: "8px",
                                                background: "#fffbf5",
                                                border: "1px solid #ebdcc5",
                                                fontSize: "0.75rem"
                                            }}>
                                                <span style={{ fontWeight: "800", color: "#4a2c11" }}>{cat.label}</span>
                                                <span style={{ color: "#ea580c", fontWeight: "900", fontSize: "0.7rem", background: "#fff", padding: "2px 8px", borderRadius: "20px", border: "1px dashed #fdba74" }}>
                                                    {cat.minAge === cat.maxAge 
                                                        ? `${cat.minAge} anos` 
                                                        : cat.maxAge >= 150 
                                                        ? `+${cat.minAge} anos`
                                                        : `${cat.minAge} a ${cat.maxAge} anos`}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </>
                )}
            </div>

            {/* Injetar estilos CSS responsivos e de animação rápidos */}
            <style>
                {`
                .ranking-header-container {
                    max-width: 1100px;
                    margin: 40px auto 30px auto;
                    border-radius: 24px;
                    overflow: hidden;
                    border: 2px solid #4a2c11;
                    box-shadow: 0 20px 40px rgba(74, 44, 17, 0.15);
                    position: relative;
                    background: #2d1705;
                }
                
                .ranking-cover-image-wrapper {
                    width: 100%;
                    height: 360px;
                    position: relative;
                    overflow: hidden;
                }
                
                .ranking-title-text {
                    font-size: 2.6rem;
                    font-weight: 900;
                    color: #fff;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin: 12px 0 4px 0;
                    text-shadow: 0 2px 10px rgba(0,0,0,0.6);
                }

                .ranking-subtitle-text {
                    margin: 0;
                    color: #fbd5a9;
                    font-size: 0.98rem;
                    text-shadow: 0 1px 5px rgba(0,0,0,0.5);
                    font-weight: 500;
                }

                .ranking-main-grid {
                    display: grid;
                    grid-template-columns: 1fr 280px;
                    gap: 25px;
                    align-items: start;
                    margin-bottom: 30px;
                }

                .ranking-content-column {
                    min-width: 0;
                }

                .ranking-sidebar-column {
                    position: sticky;
                    top: 20px;
                }

                .ranking-filter-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 15px;
                    align-items: center;
                }

                .ranking-filter-item {
                    flex: 1;
                    min-width: 150px;
                }

                .ranking-filter-item.search {
                    flex: 2;
                    min-width: 220px;
                }

                /* Custom scrollbar para visual premium na lateral */
                .custom-scroll::-webkit-scrollbar {
                    width: 5px;
                }
                .custom-scroll::-webkit-scrollbar-track {
                    background: rgba(74, 44, 17, 0.02);
                    border-radius: 10px;
                }
                .custom-scroll::-webkit-scrollbar-thumb {
                    background: rgba(234, 88, 12, 0.2);
                    border-radius: 10px;
                }
                .custom-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(234, 88, 12, 0.4);
                }

                @keyframes pulseWarning {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(234, 88, 12, 0.3); }
                    70% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(234, 88, 12, 0); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(234, 88, 12, 0); }
                }

                @media (max-width: 991px) {
                    .ranking-main-grid {
                        grid-template-columns: 1fr !important;
                        gap: 20px !important;
                    }
                    .ranking-sidebar-column {
                        position: static !important;
                        margin-top: 10px;
                    }
                }

                @media (max-width: 768px) {
                    .ranking-header-container {
                        margin: 15px auto 20px auto;
                        border-radius: 16px;
                    }
                    .ranking-cover-image-wrapper {
                        height: 200px;
                    }
                    .ranking-title-text {
                        font-size: 1.45rem;
                        margin: 8px 0 2px 0;
                        line-height: 1.3;
                    }
                    .ranking-subtitle-text {
                        font-size: 0.78rem;
                    }
                    .ranking-badge-pill {
                        font-size: 0.65rem !important;
                        padding: 3px 10px !important;
                    }
                    .ranking-stat-grid {
                        grid-template-columns: 1fr !important;
                        gap: 12px !important;
                        margin-bottom: 20px !important;
                    }
                    .ranking-filter-grid {
                        grid-template-columns: 1fr !important;
                        gap: 12px !important;
                    }
                    .ranking-filter-row {
                        flex-direction: column;
                        align-items: stretch !important;
                        gap: 12px !important;
                    }
                    .ranking-filter-item, .ranking-filter-item.search {
                        flex: 1 1 100% !important;
                        width: 100% !important;
                        min-width: 100% !important;
                    }
                    .ranking-table th {
                        padding: 8px 6px !important;
                        font-size: 0.65rem !important;
                    }
                    .ranking-table td {
                        padding: 10px 6px !important;
                        font-size: 0.8rem !important;
                    }
                    .ranking-table-pos {
                        width: 45px !important;
                    }
                    .ranking-pos-badge {
                        width: 28px !important;
                        height: 28px !important;
                        font-size: 0.75rem !important;
                        border-width: 1px !important;
                    }
                    .ranking-distance-text {
                        font-size: 1.05rem !important;
                    }
                    .ranking-distance-unit {
                        font-size: 0.7rem !important;
                    }
                    .ranking-athlete-name {
                        font-size: 0.85rem !important;
                    }
                    .ranking-athlete-sub {
                        font-size: 0.65rem !important;
                    }
                    .ranking-category-badge {
                        padding: 2px 6px !important;
                        font-size: 0.65rem !important;
                    }
                    .hide-on-mobile {
                        display: none !important;
                    }
                }
                `}
            </style>
            {/* Modal de Impressão do Certificado */}
            {selectedAthlete && (
                <CertificatePrintModal 
                    isOpen={isCertModalOpen}
                    toggle={() => setIsCertModalOpen(!isCertModalOpen)}
                    athlete={selectedAthlete}
                />
            )}
        </div>
    );
};

export default CompetitionRankingPage;
